<template>
  <!-- Teleported to the body so nested stacking contexts in the app shell
       cannot paint page content over the dialog. -->
  <teleport to="body">
    <div
      v-if="isVisible"
      class="cookie-consent-overlay tw-flex items-center justify-center p-4"
      @mousedown.self.prevent
    >
      <!--
        Deliberately not dismissible: there is no close button, the backdrop
        does not react to clicks and Escape does nothing. The visitor has to
        accept, decline or set preferences before using the platform.
      -->
      <div
        id="cookie-panel"
        ref="dialog"
        class="cookie-consent w-full max-w-xl bg-white rounded-2xl shadow-2xl tw-flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-text"
        tabindex="-1"
      >
        <div class="tw-flex items-start gap-4 p-6 sm:px-8 sm:pt-8 pb-4">
          <div class="cookie-consent__icon w-11 h-11 rounded-xl tw-flex items-center justify-center shrink-0">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M21.5 12a9.5 9.5 0 1 1-9.5-9.5 4 4 0 0 0 4 4 4 4 0 0 0 4 4c.53 0 1.03-.1 1.5-.28"
              />
              <circle cx="9" cy="10" r="1.1" fill="currentColor" stroke="none"/>
              <circle cx="13.5" cy="15" r="1.1" fill="currentColor" stroke="none"/>
              <circle cx="8.5" cy="15.5" r="1" fill="currentColor" stroke="none"/>
            </svg>
          </div>

          <h2 id="cookie-consent-title" class="text-lg sm:text-xl font-bold text-slate-900 m-0 pt-1">
            {{ $t('cookie_consent_title') }}
          </h2>
        </div>

        <!-- The explanation can be long, so it scrolls while the choice below
             stays in view: nobody has to hunt for the buttons. -->
        <div id="cookie-consent-text" class="cookie-consent__body px-6 sm:px-8">
          <p class="text-sm text-slate-600 leading-relaxed m-0">{{ $t('cookie_consent_intro') }}</p>

          <p class="text-sm text-slate-600 leading-relaxed mt-3 mb-0">
            {{ $t('cookie_consent_outro') }}
          </p>

          <p class="text-sm text-slate-600 leading-relaxed mt-3 mb-0">
            {{ $t('cookie_policy_1') }}
            <a
              :href="cookiePolicyUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary font-semibold underline"
            >{{ $t('cookie_policy') }}</a>
            {{ $t('cookie_policy_2') }}
          </p>
        </div>

        <div class="cookie-consent__footer p-6 sm:px-8 sm:pb-8 pt-4">
          <!-- This dialog cannot be dismissed, so a failed save has to be
               visible: otherwise the visitor is stuck with no explanation. -->
          <p v-if="error" class="cookie-consent__error" role="alert">
            {{ $t('cookie_consent_error') }}
          </p>

          <!--
            Reject and Accept are deliberately styled identically and both are a
            single click: refusing has to be as easy, and as visually obvious,
            as accepting.
          -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              class="cookie-consent__button cookie-consent__button--decision"
              :disabled="isSaving"
              @click="reject"
            >
              {{ $t('cookie_consent_reject_all') }}
            </button>
            <button
              type="button"
              class="cookie-consent__button cookie-consent__button--decision"
              :disabled="isSaving"
              @click="accept"
            >
              {{ $t('cookie_consent_accept_all') }}
            </button>
          </div>

          <button
            type="button"
            class="cookie-consent__button cookie-consent__button--manage"
            :disabled="isSaving"
            @click="openPreferences"
          >
            {{ $t('cookie_consent_manage') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
  import { acceptAll, consentState, rejectAll } from '../../util/cookie-consent'
  import { DEFAULT_URLS } from '../../constants/default-urls.js'
  import { lockScroll, unlockScroll } from './scroll-lock'
  import { focusFirst, trapTab } from './focus-trap'

  export default {
    name: 'CookieConsentModal',
    data () {
      return {
        consent: consentState(),
        /** True while the preferences dialog has taken over the screen. */
        preferencesOpen: false,
        error: false,
      }
    },
    computed: {
      isVisible () {
        // Never cover the policy documents the visitor needs in order to decide,
        // and never stack two overlays on top of each other — `deferred` is set
        // while a legal policy announcement holds the screen.
        if (this.isPolicyDocumentRoute || this.preferencesOpen || this.consent.deferred) {
          return false
        }

        return this.consent.decision === null
      },
      isSaving () {
        return this.consent.saving
      },
      isPolicyDocumentRoute () {
        return ['terms-of-use', 'privacy-policy', 'cookie-policy'].includes(this.$route.name)
      },
      cookiePolicyUrl () {
        return this.$store.state.config.cookiePolicyUrl || DEFAULT_URLS.COOKIE_POLICY
      },
    },
    methods: {
      accept () {
        return this.persist(acceptAll)
      },
      reject () {
        return this.persist(rejectAll)
      },
      async persist (save) {
        this.error = false

        try {
          await save()
        } catch (e) {
          // Leave the buttons live so the visitor can simply try again.
          this.error = true
          console.error('[COOKIE CONSENT] saving the decision failed', e)
        }
      },
      openPreferences () {
        this.$bus.emit('open-cookie-preferences')
      },
      onKeydown (event) {
        trapTab(event, this.$refs.dialog)
      },
      /** Lock the page and take over the keyboard for as long as we are up. */
      applyVisibility (visible) {
        if (visible) {
          lockScroll()
          // Bound on the document: a backdrop click would otherwise move focus
          // to the body, past a handler bound to the overlay.
          document.addEventListener('keydown', this.onKeydown, true)
          this.$nextTick(() => focusFirst(this.$refs.dialog))
        } else {
          unlockScroll()
          document.removeEventListener('keydown', this.onKeydown, true)
        }
      },
      onPreferencesOpened () {
        this.preferencesOpen = true
      },
      onPreferencesClosed () {
        this.preferencesOpen = false
      },
    },
    watch: {
      isVisible (visible) {
        this.applyVisibility(visible)
      },
    },
    mounted () {
      // The preferences dialog announces itself, so this also covers the entry
      // points outside the SPA (window.openCookiePreferences).
      this.$bus.on('cookie-preferences-opened', this.onPreferencesOpened)
      this.$bus.on('cookie-preferences-closed', this.onPreferencesClosed)

      // Consent is usually still pending on the very first render.
      if (this.isVisible) {
        this.applyVisibility(true)
      }
    },
    beforeUnmount () {
      this.$bus.off('cookie-preferences-opened', this.onPreferencesOpened)
      this.$bus.off('cookie-preferences-closed', this.onPreferencesClosed)

      if (this.isVisible) {
        unlockScroll()
      }

      document.removeEventListener('keydown', this.onKeydown, true)
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  .cookie-consent-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    overflow-y: auto;
    background-color: rgba(15, 23, 42, .6);
    backdrop-filter: blur(3px);
    animation: cookie-consent-fade .2s ease-out;
  }

  .cookie-consent {
    max-height: calc(100vh - 2rem);
    font-family: "Fira Sans", sans-serif;
    animation: cookie-consent-rise .24s cubic-bezier(.16, 1, .3, 1);

    &:focus {
      outline: none;
    }
  }

  .cookie-consent__body {
    overflow-y: auto;
    flex: 1 1 auto;
  }

  /* Separates the choice from the explanation it scrolls over. */
  .cookie-consent__footer {
    border-top: 1px solid #F1F5F9;
  }

  .cookie-consent__icon {
    background-color: var(--primary-community-extra-lightest, #f1f5f9);
    color: $color-primary;
  }

  .cookie-consent__error {
    margin: 0 0 .75rem;
    padding: .625rem .75rem;
    border-radius: .5rem;
    background-color: #FEF2F2;
    color: #B91C1C;
    font-size: .8125rem;
  }

  .cookie-consent__button {
    padding: .625rem 1rem;
    border-radius: .625rem;
    border: 1px solid transparent;
    font-size: .875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color .15s ease, color .15s ease, border-color .15s ease;

    &:disabled {
      opacity: .55;
      cursor: default;
    }
  }

  /* Both decision buttons share one style, so neither is nudged. */
  .cookie-consent__button--decision {
    background-color: $color-primary;
    border-color: $color-primary;
    color: white;

    &:hover:not(:disabled) {
      background-color: $color-primary-lighter;
      border-color: $color-primary-lighter;
    }
  }

  .cookie-consent__button--manage {
    display: block;
    width: 100%;
    margin-top: .75rem;
    background-color: transparent;
    color: #64748B;

    &:hover:not(:disabled) {
      background-color: #F8FAFC;
      color: #334155;
    }
  }

  @keyframes cookie-consent-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes cookie-consent-rise {
    from {
      opacity: 0;
      transform: translateY(12px) scale(.97);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .cookie-consent-overlay,
    .cookie-consent {
      animation: none;
    }
  }
</style>
